// City-helper suggestions — spec §8: a seat in `suggest` auto-manage
// mode gets the sim's top 2 build/train ideas as buttons. The sim never
// executes them; clicking routes through the normal start_building
// intent (the wasm infers units from the id).

import type { CitySuggestion } from '@aod/sim-adapter';
import { Button } from '@aod/ui/components/ui/button';
import { Separator } from '@aod/ui/components/ui/separator';
import { Hammer, Lightbulb } from 'lucide-react';
import { useT } from '../../i18n/index.js';
import { suggestionAction } from '../../sim/useSimWorld/suggestions.js';
import type { BuildingDef } from './BuildingSection.js';

interface Props {
  readonly suggestions: readonly CitySuggestion[];
  /** Catalog for resolving a `structure_id` to its display name. */
  readonly buildings: ReadonlyArray<BuildingDef>;
  readonly onApply: (idea: CitySuggestion) => void;
}

export function SuggestSection({ suggestions, buildings, onApply }: Props) {
  const t = useT();
  // Malformed ideas (wrong function, missing ids) are dropped first so the
  // §8 cap of two keeps only renderable buttons — slicing first could let
  // junk evict a good idea.
  const ideas = suggestions.filter((idea) => suggestionAction(idea) !== null).slice(0, 2);
  if (ideas.length === 0) return null;

  const label = (idea: CitySuggestion): string =>
    idea.function_name === 'train_unit'
      ? t('suggest.train', { name: String(idea.args.unit_kind ?? '') })
      : t('building.build', {
          name:
            buildings.find((b) => b.id === idea.args.structure_id)?.name ??
            String(idea.args.structure_id ?? ''),
        });

  return (
    <>
      <Separator />
      <div className="flex flex-col gap-1.5" data-testid="suggest-section">
        <span className="text-[11px] font-medium text-muted-foreground">{t('suggest.title')}</span>
        <div className="flex flex-col gap-1">
          {ideas.map((idea, i) => (
            <Button
              key={`${idea.function_name}-${i}`}
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2 text-[11px]"
              onClick={() => onApply(idea)}
              aria-label={label(idea)}
              title={idea.reasoning ?? undefined}
            >
              <Lightbulb className="size-3 text-yellow-400/80" aria-hidden />
              <span className="flex-1 truncate text-left">{label(idea)}</span>
              <Hammer className="size-3 text-muted-foreground/60" aria-hidden />
            </Button>
          ))}
        </div>
      </div>
    </>
  );
}
